import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { insightsApi } from '../../lib/insightsApi';
import { messageErreur } from '../../lib/erreurs';
import { libelle } from '../../lib/libelles';
import { formatNombre, formatPourcent } from '../../lib/formats';
import { Aide } from '../ui/aide';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { LoadingState } from '../ui/loading-state';

/**
 * Recalibrage assisté du barème d'une cuve (lot 037).
 *
 * Montre la correction proposée à partir des écarts de cuve (30 jours au
 * moins) et les lignes du barème avant / après. Seul l'administrateur
 * l'applique ; l'empreinte de la proposition vue part avec la demande, le
 * serveur refuse si le barème ou la proposition a changé entre-temps.
 */

const CELLULES = 'w-full text-sm [&_td]:px-3 [&_td]:py-1.5 [&_th]:px-3 [&_th]:py-1.5 [&_th]:text-xs [&_th]:font-medium [&_th]:text-muted-foreground';

const TON = { READY: 'warning', NOT_NEEDED: 'success', APPLIED: 'success' };

export const RecalibrageDialog = ({ open, onOpenChange, stationId, cuve, estAdministrateur, onApplied }) => {
  const [proposition, setProposition] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [enCours, setEnCours] = useState(false);

  const charger = useCallback(async () => {
    if (!stationId || !cuve?.id) return;
    setChargement(true);
    try {
      setProposition(await insightsApi.recalibration(stationId, cuve.id));
    } catch (probleme) {
      toast.error(messageErreur(probleme, 'Impossible de calculer la correction du barème'));
      setProposition(null);
    } finally {
      setChargement(false);
    }
  }, [stationId, cuve?.id]);

  useEffect(() => {
    if (open) {
      setProposition(null);
      charger();
    }
  }, [open, charger]);

  const appliquer = async () => {
    setEnCours(true);
    try {
      await insightsApi.appliquerRecalibration(stationId, cuve.id, proposition.fingerprint);
      toast.success('Barème de la cuve corrigé');
      onApplied?.();
      onOpenChange(false);
    } catch (probleme) {
      toast.error(messageErreur(probleme, 'Impossible d’appliquer la correction'));
      charger();
    } finally {
      setEnCours(false);
    }
  };

  const avant = proposition?.current_points || [];
  const apres = proposition?.proposed_points || [];
  const pret = proposition?.status === 'READY';
  const signe = Number(proposition?.correction_percent) > 0 ? '+' : Number(proposition?.correction_percent) < 0 ? '−' : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1">
            Recalibrer le barème {cuve ? `· ${cuve.name}` : ''}
            <Aide titre="Recalibrage du barème">
              La correction est calculée sur les écarts de cuve des 30 derniers jours au moins, depuis le dernier
              changement de barème. Le nouveau barème remplace l’ancien, qui reste dans le journal d’audit. Les
              jaugeages déjà enregistrés ne changent pas.
            </Aide>
          </DialogTitle>
        </DialogHeader>
        {chargement || !proposition ? (
          <LoadingState shape="readouts" />
        ) : (
          <div className="grid gap-4">
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-muted/40 px-3 py-2 text-sm">
              <Badge variant={TON[proposition.status] || 'muted'}>{libelle('station_recalibrage', proposition.status)}</Badge>
              <span className="text-muted-foreground tabular-nums">
                {formatNombre(proposition.days, 0)} jours mesurés sur {formatNombre(proposition.span_days, 0)}
              </span>
              {proposition.correction_percent !== null && proposition.correction_percent !== undefined && (
                <span className="font-semibold tabular-nums">
                  {signe}
                  {formatPourcent(Math.abs(Number(proposition.correction_percent)), 2)}
                </span>
              )}
            </div>
            {pret && (
              <div className="max-h-64 overflow-auto rounded-lg border">
                <table className={CELLULES}>
                  <thead>
                    <tr>
                      <th className="text-right">Hauteur (cm)</th>
                      <th className="text-right">Litres avant</th>
                      <th className="text-right">Litres après</th>
                    </tr>
                  </thead>
                  <tbody>
                    {avant.map((ligne, rang) => (
                      <tr key={`${ligne[0]}-${rang}`}>
                        <td className="text-right tabular-nums">{formatNombre(ligne[0], 0)}</td>
                        <td className="text-right tabular-nums">{formatNombre(ligne[1], 0)}</td>
                        <td className="text-right tabular-nums">{formatNombre(apres[rang]?.[1], 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
          {estAdministrateur && (
            <Button type="button" onClick={appliquer} disabled={!pret || enCours}>
              Appliquer la correction
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RecalibrageDialog;
